/**
 * Thin FE wrappers for `/api/v1/runs/*`.
 *
 * Shape notes:
 *
 *  - `listRuns` returns the full paginated envelope (`items`, `total`,
 *    `page`, `pageSize`). Callers in the table UI need the total to render
 *    pagination controls — stripping it here would force every page to call
 *    HEAD separately.
 *  - `createRun` takes a browser `File`. We wrap it in `FormData` ourselves
 *    so the route layer (T-051+) just calls `createRun(file)`.
 *  - Download endpoints do NOT flow through `apiFetch` — the server issues
 *    302 redirects to short-lived R2 URLs and browser fetch-follows-redirect
 *    would leak cookies / CORS into R2's origin. `downloadUrl` builds a
 *    string the caller can hand to `<a href>`; the browser follows the 302
 *    natively and the session cookie is sent on the initial request only.
 */
import { runs } from '@dragonis-cdr/shared';

import { apiFetch } from './client';

export type RunStatus = runs.RunStatus;
export type RunListItem = runs.RunListItem;
export type RunListResponse = runs.RunListResponse;
export type RunDetail = runs.RunDetail;
export type CreateRunResponse = runs.CreateRunResponse;

export interface ListRunsParams {
  page?: number;
  pageSize?: number;
}

function buildQuery(params: Record<string, number | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, number] => {
    const value = entry[1];
    return typeof value === 'number' && Number.isFinite(value);
  });
  if (entries.length === 0) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of entries) {
    usp.set(key, String(value));
  }
  return `?${usp.toString()}`;
}

export async function listRuns(params: ListRunsParams = {}): Promise<RunListResponse> {
  const query = buildQuery({
    page: params.page,
    pageSize: params.pageSize,
  });
  const body = await apiFetch<unknown>(`/runs${query}`);
  return runs.RunListResponse.parse(body);
}

export async function getRun(id: string): Promise<RunDetail> {
  const body = await apiFetch<unknown>(`/runs/${encodeURIComponent(id)}`);
  return runs.RunDetail.parse(body);
}

/**
 * POST /api/v1/runs — multipart upload of a CSV. Returns the new run's id
 * and initial status (PENDING). The worker flips it through PARSING /
 * RENDERING asynchronously; callers poll via `getRun`.
 */
export async function createRun(file: File): Promise<CreateRunResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  const body = await apiFetch<unknown>('/runs', {
    method: 'POST',
    body: form,
  });
  return runs.CreateRunResponse.parse(body);
}

/**
 * POST /api/v1/runs/:id/regenerate — creates a new run reusing the source
 * CSV. The source row is not touched; audit entries track the link.
 */
export async function regenerateRun(id: string): Promise<CreateRunResponse> {
  const body = await apiFetch<unknown>(`/runs/${encodeURIComponent(id)}/regenerate`, {
    method: 'POST',
  });
  return runs.CreateRunResponse.parse(body);
}

/**
 * Build the server-relative URL for a download. The server responds with a
 * 302 to a signed R2 URL; the caller either:
 *   - puts this URL in an `<a href>` for a click-to-download UX, or
 *   - calls `window.location.assign(url)` to trigger a same-tab navigation.
 *
 * We do NOT fetch this from JS: the 302 target is cross-origin (R2) and the
 * XHR/fetch redirect path needs preflight + exposed-headers that R2 does not
 * emit for signed GETs. The native browser navigation flow is the only
 * sanctioned consumer.
 */
export type DownloadKind = 'csv' | 'bundle' | { pdfCustomerId: string };

export function downloadUrl(runId: string, kind: DownloadKind): string {
  const safeRun = encodeURIComponent(runId);
  if (kind === 'csv') return `/api/v1/runs/${safeRun}/csv`;
  if (kind === 'bundle') return `/api/v1/runs/${safeRun}/bundle`;
  const safeCustomer = encodeURIComponent(kind.pdfCustomerId);
  return `/api/v1/runs/${safeRun}/pdfs/${safeCustomer}`;
}
